import type { IAddArticleToCartCmd } from "@/data/commands/customer/AddArticleToCartCmd";
import type { IApplyDiscountToBillCmd } from "@/data/commands/customer/ApplyDiscountToBillCmd";
import type { IApplyDiscountToCartCmd } from "@/data/commands/customer/ApplyDiscountToCartCmd";
import type { ICheckoutBillCmd } from "@/data/commands/customer/CheckoutBillCmd";
import type { ICheckoutCartCmd } from "@/data/commands/customer/CheckoutCartCmd";
import type { ICustomerReceiveFulfillmentCmd } from "@/data/commands/customer/CustomerReceiveFulfillmentCmd";
import type { ICustomerSendReceiptCmd } from "@/data/commands/customer/CustomerSendReceiptCmd";
import type { IPrepareBillForCheckoutCmd } from "@/data/commands/customer/PrepareBillForCheckoutCmd";
import type { IPrepareCartForCheckoutCmd } from "@/data/commands/customer/PrepareCartForCheckoutCmd";
import type { IRemoveAllBillVouchersCmd } from "@/data/commands/customer/RemoveAllBillDiscountsCmd";
import type { IRemoveAllCartDiscountsCmd } from "@/data/commands/customer/RemoveAllCartDiscountsCmd";
import type { IRemoveAllCartVouchersCmd } from "@/data/commands/customer/RemoveAllCartVouchersCmd";
import type { IRemoveBillItemDiscountsCmd } from "@/data/commands/customer/RemoveBillItemDiscountsCmd";
import type { IRemoveCartItemCommentCmd } from "@/data/commands/customer/RemoveCartItemCommentCmd";
import type { IRemoveCartItemDiscountsCmd } from "@/data/commands/customer/RemoveCartItemDiscountsCmd";
import type { IRemoveItemFromCartCmd } from "@/data/commands/customer/RemoveItemFromCartCmd";
import type { ISelectFulfillmentSlotCmd } from "@/data/commands/customer/SelectFulfillmentSlotCmd";
import type { ISetCartItemCommentCmd } from "@/data/commands/customer/SetCartItemCommentCmd";
import type { ISetCartItemQuantityCmd } from "@/data/commands/customer/SetCartItemQuantityCmd";
import type { IStartPricePerMinuteTimerCmd } from "@/data/commands/customer/StartPricePerMinuteTimerCmd";
import type { IStopPricePerMinuteTimerCmd } from "@/data/commands/customer/StopPricePerMinuteTimerCmd";
import type { ICreateOrUpdateDeliveryAddress, IUpdateOrCreateOrderCustomerContactDataCmd } from "@/data/commands/profile/UpdateProfileCmd";
import type { IAllocateOperatorToTableCmd, IAllocateToTableByOrder, IAllocateToTableCmd, IAllocateToTableWithCodeCmd } from "@/data/commands/table/AllocateToTableCmd";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import type { IBill, IBillDiscount, IBillDiscountItemSplit, IBillDiscountSummary, IBillItem, IBillItemDiscount, IBillItemDiscountSummary, IBillItemOption, IBillItemOptionGroup, IBillItemSelectionAmounts, IBillItemTimerDiscount, IUserItemSelection } from "@/data/model/Bill";
import type { IArticleIconAttributes, ICartDiscount, ICartDiscountsSummary, ICartItem, ICartItemDiscountSummary, ICartItemOptionGroup, ICustomerArticle, ICustomerArticleAttribute, ICustomerArticleResource, ICustomerCart, ICustomerCategory, ICustomerFulfillment, ICustomerOffer, ICustomerOptionGroup, ICustomerOptionGroupOption, ICustomerOrder, IDeliveryAddress, IFulfillmentDateSlotDto, IFulfillmentTimeSlotDto, IFullCustomerOrder, IOrderedArticles, IOrderPaymentConfirmation, IPaymentInformation, IPaymentIntent, IPaymentIntentConfirmHandle, IServicePaymentMethods, IWalleeTransactionLineToAuthorize, IWalleeTransactionToAuthorize, IWorldlineTerminalPaymentStatus, IWorldlineTransactionToAuthorize } from "@/data/model/Customer";
import type { ICustomerPayment, ICustomerPaymentItem } from "@/data/model/CustomerPayment";
import { AttributeDisplayType, Language } from "@/data/model/Enums";
import type { IContactData, IProfile } from "@/data/model/Profile";
import type { ITableAllocation } from "@/data/model/Table";
import type { IArticleOptionsQuery } from "@/data/queries/customer/ArticleOptionsQry";
import type { ICustomerOfferQry } from "@/data/queries/customer/CurrentOfferQry";
import type { ICustomerOrdersQry, IGetCurrentBillQry } from "@/data/queries/customer/CustomerOrdersQry";
import type { ICustomerPaymentsQry } from "@/data/queries/customer/CustomerPaymentsQry";
import type { IGetAvailableFulfillmentSlotsQry } from "@/data/queries/customer/GetAvailableFulfillmentSlotsQry";
import type { IGetCartDiscountsSummaryQry } from "@/data/queries/customer/GetCartDiscountsSummaryQry";
import type { ICanOrderBeCanceledQry } from "@/data/queries/order/ICanOrderBeCanceledQry";
import { DateTime } from "@/data/util/DateTime";
import { Misc } from "@/data/util/Misc";



import { BaseServiceDao, type IPaginatedResult, parseCollection, parseMultiLanguageText, parsePaginatedResult } from "./BaseServiceDao";
import { LocationDao } from "./LocationDao";
import { OrderDao } from "./OrderDao";
import { PlanningDao } from "./PlanningDao";
import { RoomDao } from "./RoomDao";


export class CustomerDao extends BaseServiceDao {
    public static className = "CustomerDao";

    constructor() {
        super("customer");
    }

    public async getCurrentAllocation(): Promise<ITableAllocation> {
        return this.get("allocation", undefined, CustomerDao.parseTableAllocation);
    }

    public async allocateToTable(cmd: IAllocateToTableCmd): Promise<ITableAllocation> {
        return this.post("allocate", cmd, CustomerDao.parseTableAllocation);
    }

    public async allocateOperatorToTable(cmd: IAllocateOperatorToTableCmd): Promise<ITableAllocation> {
        return this.post("allocate-operator", cmd, CustomerDao.parseTableAllocation);
    }

    public async allocateToTableByOrderId(cmd: IAllocateToTableByOrder): Promise<ITableAllocation> {
        return this.post("allocate-by-order", cmd, CustomerDao.parseTableAllocation);
    }

    public async allocateToTableWithCode(cmd: IAllocateToTableWithCodeCmd): Promise<ITableAllocation> {
        return this.post("allocate/" + cmd.code, {}, CustomerDao.parseTableAllocation);
    }

    public async deallocateFromTable(): Promise<void> {
        return this.post("deallocate");
    }

    public async getOffer(qry: ICustomerOfferQry): Promise<ICustomerOffer> {
        return this.get("offer", qry, CustomerDao.parseCustomerOffer);
    }

    public async getArticleAttributes(locationId: string, language: Language): Promise<IArticleIconAttributes> {
        return this.get("article-attributes", { locationId: locationId, language: language }, CustomerDao.parseArticleIconAttributes);
    }

    // the server decides by the role which orders count: the end user's own at the location, or for an operator the
    // ones they placed themselves on the bill of their table allocation
    public async getOrderedArticles(locationId: string): Promise<IOrderedArticles> {
        return this.get("ordered-articles", { locationId: locationId });
    }

    public async getCart(): Promise<ICustomerCart> {
        return this.get("cart", {}, CustomerDao.parseCustomerCart);
    }

    public async addArticleToCart(cmd: IAddArticleToCartCmd): Promise<ICustomerCart> {
        return this.post("cart/article", cmd, CustomerDao.parseCustomerCart);
    }

    public async setItemQuantityInCart(id: string, cmd: ISetCartItemQuantityCmd): Promise<ICustomerCart> {
        return this.put(`cart/item/${id}`, cmd, CustomerDao.parseCustomerCart);
    }

    public async removeItemFromCart(id: string, cmd: IRemoveItemFromCartCmd): Promise<ICustomerCart> {
        return this.delete(`cart/item/${id}`, cmd, CustomerDao.parseCustomerCart);
    }

    public async setCartItemComment(id: string, cmd: ISetCartItemCommentCmd): Promise<ICustomerCart> {
        return this.put(`cart/item/${id}/comment`, cmd, CustomerDao.parseCustomerCart);
    }

    public async removeCartItemComment(id: string, cmd: IRemoveCartItemCommentCmd): Promise<ICustomerCart> {
        return this.delete(`cart/item/${id}/comment`, cmd, CustomerDao.parseCustomerCart);
    }

    public async applyDiscountToCart(cmd: IApplyDiscountToCartCmd): Promise<ICustomerCart> {
        return this.post("cart/discount/apply", cmd, CustomerDao.parseCustomerCart);
    }

    public async removeAllCartDiscounts(cmd: IRemoveAllCartDiscountsCmd = {}): Promise<ICustomerCart> {
        return this.delete("cart/discount/remove-all", cmd, CustomerDao.parseCustomerCart);
    }

    public async removeAllCartVouchers(cmd: IRemoveAllCartVouchersCmd = {}): Promise<ICustomerCart> {
        return this.delete("cart/voucher/remove-all", cmd, CustomerDao.parseCustomerCart);
    }

    public async removeCartItemDiscounts(id: string, cmd: IRemoveCartItemDiscountsCmd): Promise<ICustomerCart> {
        return this.delete(`cart/item/${id}/discount`, cmd, CustomerDao.parseCustomerCart);
    }

    public async removeCartDiscount(cartId: string, discountId: string): Promise<ICustomerCart> {
        return this.delete(`cart/${cartId}/discount/${discountId}`, {}, CustomerDao.parseCustomerCart);
    }

    public async removeCartVoucher(cartId: string, voucherId: string): Promise<ICustomerCart> {
        return this.delete(`cart/${cartId}/voucher/${voucherId}`, {}, CustomerDao.parseCustomerCart);
    }

    public async removeCartItemDiscount(cartId: string, discountId: string): Promise<ICustomerCart> {
        return this.removeCartDiscount(cartId, discountId);
    }

    public async getOptionsOfArticle(qry: IArticleOptionsQuery): Promise<Array<ICustomerOptionGroup>> {
        return this.get(
            `article/${qry.article}/option`,
            {
                locationId: qry.locationId,
                offerId: qry.offerId,
                useCaseId: qry.useCaseId,
            },
            (c) => parseCollection(c, CustomerDao.parseCustomerOptionGroup)
        );
    }

    public async getCartDiscountsSummary(cmd: IGetCartDiscountsSummaryQry = {}): Promise<ICartDiscountsSummary> {
        return this.get("cart/discount/summary", cmd, CustomerDao.parseCartDiscountsSummary);
    }

    public async removeCartsFromLocation(): Promise<void> {
        return this.delete("cart/from-location");
    }

    public async prepareCartForCheckout(cmd: IPrepareCartForCheckoutCmd): Promise<IPaymentIntent> {
        return this.post("cart/prepare", cmd, CustomerDao.parseIntent);
    }

    public async getFulfillmentSlots(qry: IGetAvailableFulfillmentSlotsQry): Promise<Array<IFulfillmentDateSlotDto>> {
        return this.post("fulfillment-slots", qry, (c) => parseCollection(c, CustomerDao.parseFulfillmentDateSlot));
    }

    public async selectCartFulfillmentSlot(cmd: ISelectFulfillmentSlotCmd): Promise<void> {
        return this.post("cart/fulfillment-slot", cmd);
    }

    public async checkoutCart(cmd: ICheckoutCartCmd): Promise<IPaymentIntentConfirmHandle | undefined> {
        return this.post("cart/checkout", cmd, (c) =>
            c == null ? undefined : CustomerDao.parseIntentConfirmHandle(c)
        );
    }

    // orders the price per minute item with pay later, which opens the bill of the table, and starts its timer
    public async startPricePerMinuteTimer(cmd: IStartPricePerMinuteTimerCmd): Promise<void> {
        return this.post("cart/start-timer", cmd);
    }

    // stops the timer of the price per minute item, the operator who stops it is recorded on the order item
    public async stopPricePerMinuteTimer(cmd: IStopPricePerMinuteTimerCmd): Promise<void> {
        return this.post("stop-timer", cmd);
    }

    // ids of the articles with a running timer at the table of the current user
    public async getRunningTimers(): Promise<Array<string>> {
        return this.get("running-timers", undefined, (f) => parseCollection(f.articleIds, (v) => v as string));
    }

    public async getCanOrderBeCanceled(orderId: string, cmd: ICanOrderBeCanceledQry): Promise<boolean> {
        return this.get("order/" + orderId + "/can-be-canceled", cmd);
    }

    public async sendReceipt(orderId: string, cmd: ICustomerSendReceiptCmd): Promise<void> {
        return this.post("order/" + orderId + "/receipt", cmd);
    }

    public async sendBillReceipt(paymentId: string, cmd: ICustomerSendReceiptCmd): Promise<void> {
        return this.post("payment/" + paymentId + "/receipt", cmd);
    }

    public async checkOrderPaymentConfirmation(transactionId: string): Promise<IOrderPaymentConfirmation> {
        return this.get("order/check/" + transactionId, undefined, CustomerDao.parseOrderPaymentConfirmation);
    }

    public async checkOrderPaymentConfirmationByExternalPaymentId(
        externalPaymentId: string
    ): Promise<IOrderPaymentConfirmation> {
        return this.get(
            "order/check-by-external-payment-id/" + externalPaymentId,
            undefined,
            CustomerDao.parseOrderPaymentConfirmation
        );
    }

    public async getOrder(id: string, abortSignal?: AbortSignal): Promise<IFullCustomerOrder> {
        return this.get("order/" + id, undefined, CustomerDao.parseFullCustomerOrder, abortSignal);
    }

    public async getOrders(qry: ICustomerOrdersQry): Promise<IPaginatedResult<ICustomerOrder>> {
        return this.get("order/", qry, (c) => parsePaginatedResult(c, CustomerDao.parseCustomerOrder));
    }

    public async getPayments(qry: ICustomerPaymentsQry): Promise<IPaginatedResult<ICustomerPayment>> {
        return this.get("payment/", qry, (c) => parsePaginatedResult(c, CustomerDao.parseCustomerPayment));
    }

    public async getActiveFulfillments(): Promise<Array<ICustomerFulfillment>> {
        return this.get("order/fulfillment", undefined, (c) =>
            parseCollection(c, CustomerDao.parseCustomerOrderFulfillment)
        );
    }

    public async receiveFulfillment(id: string, cmd: ICustomerReceiveFulfillmentCmd): Promise<void> {
        return this.post("order/fulfillment/" + id + "/received", cmd);
    }

    public async getCurrentBill(qry: IGetCurrentBillQry): Promise<IBill | undefined> {
        return this.get("bill", qry, CustomerDao.parseNullableBill);
    }

    public async applyDiscountToBill(cmd: IApplyDiscountToBillCmd): Promise<IBill> {
        return this.post("bill/discount/apply", cmd, CustomerDao.parseBill);
    }

    public async removeAllBillVouchers(cmd: IRemoveAllBillVouchersCmd): Promise<IBill> {
        return this.delete("bill/voucher/remove-all", cmd, CustomerDao.parseBill);
    }

    public async removeBillDiscount(billId: string, discountId: string): Promise<IBill> {
        return this.delete(`bill/${billId}/discount/${discountId}`, {}, CustomerDao.parseBill);
    }

    public async removeBillVoucher(billId: string, voucherId: string): Promise<IBill> {
        return this.delete(`bill/${billId}/voucher/${voucherId}`, {}, CustomerDao.parseBill);
    }

    public async removeBillItemDiscount(billId: string, discountId: string): Promise<IBill> {
        return this.removeBillDiscount(billId, discountId);
    }

    public async removeBillItemDiscounts(
        billId: string,
        itemId: string,
        cmd: IRemoveBillItemDiscountsCmd
    ): Promise<IBill> {
        return this.delete(`bill/${billId}/item/${itemId}/discount`, cmd, CustomerDao.parseBill);
    }

    public async prepareBillForCheckout(cmd: IPrepareBillForCheckoutCmd): Promise<IPaymentIntent> {
        return this.post("bill/prepare", cmd, CustomerDao.parseIntent);
    }

    public async checkoutBill(cmd: ICheckoutBillCmd): Promise<IPaymentIntentConfirmHandle | undefined> {
        return this.post("bill/checkout", cmd, (c) =>
            c == null ? undefined : CustomerDao.parseIntentConfirmHandle(c)
        );
    }

    // Asks the Worldline terminal again about a payment whose outcome could not be read
    public async updateWorldlineTerminalPaymentStatus(externalPaymentId: string): Promise<IWorldlineTerminalPaymentStatus> {
        return this.post("payment/" + externalPaymentId + "/worldline/update-status", {});
    }

    public async getCustomerPayment(payment: string): Promise<ICustomerPayment> {
        return this.get("payment/" + payment, undefined, CustomerDao.parseCustomerPayment);
    }

    public async getCurrentIdentityId(): Promise<string | undefined> {
        return this.get("identity-id", undefined, (identityId) => (identityId == null ? undefined : identityId));
    }

    public async getServicePaymentMethods(serviceId: string): Promise<IServicePaymentMethods> {
        return this.get(`service/${serviceId}/payment-methods`, undefined, CustomerDao.parseServicePaymentMethods);
    }

    public async updateOrCreateOrderCustomerContactData(
        cmd: IUpdateOrCreateOrderCustomerContactDataCmd
    ): Promise<IProfile> {
        return this.put("contact-data", cmd, ProfileDao.parseProfile);
    }

    public async createOrUpdateDeliveryAddress(
        cmd: ICreateOrUpdateDeliveryAddress
    ): Promise<IDeliveryAddress> {
        return this.put("delivery-address", cmd, CustomerDao.parseDeliveryAddress);
    }

    public static parseTableAllocation(literal: any): ITableAllocation {
        return {
            allocatedSince: DateTime.parseIsoDate(literal.allocatedSince),
            locationId: literal.locationId,
            locationInternalName: literal.locationInternalName == null ? undefined : literal.locationInternalName,
            locationDesign: literal.locationDesign == null ? {} : LocationDao.parseDesign(literal.locationDesign),
            tableId: literal.tableId,
            identityId: literal.identityId,
            tableNumber: literal.tableNumber,
            locationLanguage: parseInt(literal.locationLanguage),
            services: parseCollection(literal.services, RoomDao.parseService),
            locationName: literal.locationName,
            roomId: literal.roomId != null ? literal.roomId : undefined,
            roomName: literal.roomId != null ? literal.roomName : undefined,
            roomKioskHeader:
                literal.roomKioskHeader == null ? undefined : parseMultiLanguageText(literal.roomKioskHeader),
            guestNumber: parseInt(literal.guestNumber),
            tableName: literal.tableName,
            tableSeats: parseInt(literal.tableSeats),
            country: parseInt(literal.country),
            customTranslations: literal.customTranslations != null ? literal.customTranslations : undefined,
            googleMapsLocationLink: literal.googleMapsLocationLink,
            locationAddress:
                literal.locationAddress == null ? undefined : LocationDao.parseAddress(literal.locationAddress),
            orderBillId: literal.orderBillId == null ? undefined : literal.orderBillId,
        };
    }

    private static parseCustomerOffer(literal: any): ICustomerOffer {
        return {
            locationId: literal.locationId,
            offerId: literal.offerId,
            cart: literal.cart == null ? undefined : CustomerDao.parseCustomerCart(literal.cart),
            entries: parseCollection(literal.entries, CustomerDao.parseCustomerCategory),
            // todo: this becomes required once we remove async options loading
            offerOptionGroups:
                literal.offerOptionGroups == null
                    ? undefined
                    : parseCollection(literal.offerOptionGroups, CustomerDao.parseCustomerOptionGroup),
        };
    }

    private static parseCustomerCategory(literal: any): ICustomerCategory {
        return {
            id: literal.id,
            categoryId: literal.categoryId,
            name: parseMultiLanguageText(literal.name),
            description: parseMultiLanguageText(literal.description),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            squareImagePath: literal.squareImagePath == null ? undefined : literal.squareImagePath,
            articles: parseCollection(literal.articles, CustomerDao.parseCustomerArticle),
            highlight: literal.highlight,
            highlightType: literal.highlightType == null ? undefined : parseInt(literal.highlightType),
        };
    }

    private static parseCustomerArticle(literal: any): ICustomerArticle {
        return {
            id: literal.id,
            articleId: literal.articleId,
            categoryId: literal.categoryId,
            offerId: literal.offerId,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            hasOptions: literal.hasOptions,
            shortDescription: parseMultiLanguageText(literal.shortDescription),
            longDescription: parseMultiLanguageText(literal.longDescription),
            price: literal.price == null ? undefined : parseFloat(literal.price),
            oohPrice: literal.oohPrice == null ? undefined : parseFloat(literal.oohPrice),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            originalImagePath: literal.originalImagePath == null ? undefined : literal.originalImagePath,
            kioskImagePath: literal.kioskImagePath == null ? undefined : literal.kioskImagePath,
            minQuantity: literal.minQuantity == null ? undefined : parseInt(literal.minQuantity),
            maxQuantity: literal.maxQuantity == null ? undefined : parseInt(literal.maxQuantity),
            isOpenPriceEnabled: literal.isOpenPriceEnabled,
            isNegativeOpenPrice: literal.isNegativeOpenPrice,
            isPricePerMinute: literal.isPricePerMinute ?? false,
            sortedOptionGroups: literal.sortedOptionGroups == null ? undefined : literal.sortedOptionGroups,
            resources:
                literal.resources == null
                    ? []
                    : parseCollection(literal.resources, CustomerDao.parseCustomerArticleResource),
            attributeIds: literal.attributeIds,
        };
    }

    private static parseArticleIconAttributes(literal: any): IArticleIconAttributes {
        return {
            attributes: parseCollection(literal.attributes, CustomerDao.parseCustomerArticleAttribute),
            attributeIdsByArticleId: literal.attributeIdsByArticleId ?? {},
        };
    }

    public static parseCustomerCart(literal: any): ICustomerCart {
        return {
            cartId: literal.cartId,
            hash: literal.hash,
            total: parseFloat(literal.total),
            items: parseCollection(literal.items, CustomerDao.parseCartItem),
            serviceId: literal.serviceId,
            preorderDate: literal.preorderDate == null ? undefined : DateTime.parseIsoDate(literal.preorderDate),
            preorderTime: literal.preorderTime == null ? undefined : literal.preorderTime,
            offerId: literal.offerId == null ? undefined : literal.offerId,
            hasDiscount: literal.hasDiscount,
            hasVoucherDiscount: literal.hasVoucherDiscount,
            totalDiscountedAmount: literal.totalDiscountedAmount,
            totalPayableAmount: literal.totalPayableAmount,
            contactDataEndUser:
                literal.contactDataEndUser == null
                    ? undefined
                    : CustomerDao.parseContactData(literal.contactDataEndUser),
            fees: literal.fees,
            deliveryAddress: literal.deliveryAddress == null ? undefined:CustomerDao.parseDeliveryAddress(literal.deliveryAddress),
        };
    }

    public static parseDeliveryAddress(literal: any): IDeliveryAddress {
        return {
            id: literal.id,
            street: literal.street,
            addressDetails: literal.addressDetails != null ? literal.addressDetails : undefined,
            postalCode: literal.postalCode,
            city: literal.city,
            country: literal.country,
        };
    }

    public static parseContactData(literal: any): IContactData {
        return {
            identityId: literal.identityId,
            firstName: literal.firstName != null ? literal.firstName : undefined,
            lastName: literal.lastName != null ? literal.lastName : undefined,
            email: literal.email != null ? literal.email : undefined,
            phoneNumber: literal.phoneNumber != null ? literal.phoneNumber : undefined,
        };
    }
    private static parseCartItem(literal: any): ICartItem {
        return {
            id: literal.id,
            articleId: literal.articleId,
            quantity: parseInt(literal.quantity),
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            unitPrice: parseFloat(literal.unitPrice),
            hasDiscount: literal.hasDiscount,
            unitPriceAfterDiscount: parseFloat(literal.unitPriceAfterDiscount),
            minAge: literal.minAge == null ? undefined : parseInt(literal.minAge),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            options: parseCollection(literal.options, CustomerDao.parseCartOptionGroup),
            minQuantity: literal.minQuantity == null ? undefined : parseInt(literal.minQuantity),
            maxQuantity: literal.maxQuantity == null ? undefined : parseInt(literal.maxQuantity),
            canHaveComments: literal.canHaveComments,
            comment: literal.comment == null ? undefined : literal.comment,
            lastDiscountType: literal.lastDiscountType == null ? undefined : parseInt(literal.lastDiscountType),
        };
    }

    private static parseCartOptionGroup(literal: any): ICartItemOptionGroup {
        return {
            id: literal.id,
            displayName: literal.displayName,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            items:
                literal.items == null || literal.items.length === 0
                    ? []
                    : parseCollection(literal.items, CustomerDao.parseCartItem),
        };
    }

    private static parseIntent(literal: any): IPaymentIntent {
        return {
            javascriptUrl: literal.javascriptUrl,
            paymentMethods: parseCollection(literal.paymentMethods, PlanningDao.parsePaymentMethod),
            quickPaymentMethods: parseCollection(literal.quickPaymentMethods, PlanningDao.parsePaymentMethod),
            kioskPaymentPrompt: literal.kioskPaymentPrompt != null ? parseMultiLanguageText(literal.kioskPaymentPrompt) : undefined,
            selectionConsumption: literal.selectionConsumption ?? undefined,
            selectionTotalAfterDiscount: literal.selectionTotalAfterDiscount ?? undefined,
            selectionTotalDiscountsAndVouchersAmount: literal.selectionTotalDiscountsAndVouchersAmount ?? undefined,
            areTimersStopped: literal.areTimersStopped === true,
        };
    }

    private static parseIntentConfirmHandle(literal: any): IPaymentIntentConfirmHandle {
        return {
            intentId: literal.intentId,
            stripePaymentFailed: literal.stripePaymentFailed == null ? undefined : literal.stripePaymentFailed,
            intentSecret: literal.intentSecret,
            isAlreadyConfirmed: literal.isAlreadyConfirmed,
            walleeJavascriptUrl: literal.walleeJavascriptUrl == null ? undefined : literal.walleeJavascriptUrl,
            walleeOneClickUrl: literal.walleeOneClickUrl == null ? undefined : literal.walleeOneClickUrl,
            walleeSelectedPaymentId:
                literal.walleeSelectedPaymentId == null ? undefined : literal.walleeSelectedPaymentId,
            isPaymentDevice: literal.isPaymentDevice,
            openBill: literal.openBill == null ? undefined : literal.openBill,
            isWalleePerformTransactionSucceeded: literal.isWalleePerformTransactionSucceeded,
            walleeTransactionToAuthorize:
                literal.walleeTransactionToAuthorize == null
                    ? undefined
                    : CustomerDao.parseWalleeTransactionToAuthorize(literal.walleeTransactionToAuthorize),
            worldlineTransactionToAuthorize:
                literal.worldlineTransactionToAuthorize == null
                    ? undefined
                    : CustomerDao.parseWorldlineTransactionToAuthorize(literal.worldlineTransactionToAuthorize),
            wasPaymentCoveredByDiscounts: literal.wasPaymentCoveredByDiscounts,
            operatorPaymentUrl: literal.operatorPaymentUrl == null ? undefined : literal.operatorPaymentUrl,
            stripeApplePayCheckoutSessionUrl: literal.stripeApplePayCheckoutSessionUrl == null ? undefined : literal.stripeApplePayCheckoutSessionUrl,
        };
    }

    private static parseWorldlineTransactionToAuthorize(literal: any): IWorldlineTransactionToAuthorize {
        return {
            externalPaymentId: literal.externalPaymentId,
            currency: literal.currency,
            amount: parseFloat(literal.amount),
            sessionId: literal.sessionId,
        };
    }
    private static parseWalleeTransactionToAuthorize(literal: any): IWalleeTransactionToAuthorize {
        return {
            externalPaymentId: literal.externalPaymentId,
            currency: literal.currency,
            lines: parseCollection(literal.lines, CustomerDao.parseWalleeTransactionLineToAuthorize),
        };
    }

    private static parseWalleeTransactionLineToAuthorize(literal: any): IWalleeTransactionLineToAuthorize {
        return {
            paymentItemId: literal.paymentItemId,
            amount: parseFloat(literal.amount),
        };
    }

    public static parseOrderPaymentConfirmation(literal: any): IOrderPaymentConfirmation {
        return {
            error: literal.error == null ? undefined : literal.error,
            paymentFor: parseInt(literal.paymentFor),
            currency: parseInt(literal.currency),
            amount: parseFloat(literal.amount),
            isConfirmed: literal.isConfirmed == null ? undefined : literal.isConfirmed,
            orderNumber: literal.orderNumber == null ? undefined : literal.orderNumber,
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            dailyOrderIncrement: literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            tableName: literal.tableName == null ? undefined : literal.tableName,
            orderId: literal.orderId == null ? undefined : literal.orderId,
            paymentMethodSplitInformation: parseCollection(
                literal.paymentMethodSplitInformation,
                CustomerDao.parsePaymentInformation
            ),
            orderServiceId: literal.orderServiceId == null ? undefined : literal.orderServiceId,
            paymentId: literal.paymentId == null ? undefined : literal.paymentId,
            scheduledFulfillmentDateTime:
                literal.scheduledFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.scheduledFulfillmentDateTime),
            billId: literal.billId == null ? undefined : literal.billId,
            sendEmailConfirmationsForUnpaidOrders: literal.sendEmailConfirmationsForUnpaidOrders,
            isPayLater: literal.isPayLater,
            payUseCaseId: literal.payUseCaseId == null ? undefined : literal.payUseCaseId,
            orderUseCaseId: literal.orderUseCaseId == null ? undefined : literal.orderUseCaseId,
            providerId: literal.providerId == null ? undefined : literal.providerId,
            messageToUserAfterOrderPlaced:
                literal.messageToUserAfterOrderPlaced == null
                    ? undefined
                    : parseMultiLanguageText(literal.messageToUserAfterOrderPlaced),
            deliveryAddress: literal.deliveryAddress == null ? undefined : CustomerDao.parseDeliveryAddress(literal.deliveryAddress),
        };
    }

    private static parsePaymentInformation(literal: any): IPaymentInformation {
        return {
            id: literal.id,
            amount: parseFloat(literal.amount),
            type: literal.type == null ? undefined : literal.type,
            brand: literal.brand == null ? undefined : literal.brand,
            ccDigit: literal.ccDigit == null ? undefined : literal.ccDigit,
            provider: literal.provider == null ? undefined : parseInt(literal.provider),
            merchantId: literal.merchantId == null ? undefined : literal.merchantId,
            isCardScheme: literal.isCardScheme,
            actualPaymentMethodIconUrl:
                literal.actualPaymentMethodIconUrl == null ? undefined : literal.actualPaymentMethodIconUrl,
            actualPaymentMethodName:
                literal.actualPaymentMethodName == null ? undefined : literal.actualPaymentMethodName,
            predefinedPaymentMethodIconUrl:
                literal.predefinedPaymentMethodIconUrl == null ? undefined : literal.predefinedPaymentMethodIconUrl,
            predefinedOnlinePaymentMethodName:
                literal.predefinedOnlinePaymentMethodName == null
                    ? undefined
                    : literal.predefinedOnlinePaymentMethodName,
            customPaymentMethodName:
                literal.customPaymentMethodName == null ? undefined : literal.customPaymentMethodName,
            customPaymentMethodIcon:
                literal.customPaymentMethodIcon == null ? undefined : literal.customPaymentMethodIcon,
        };
    }

    private static parseCustomerOrder(literal: any): ICustomerOrder {
        return {
            id: literal.id,
            orderNumber: literal.orderNumber,
            locationId: literal.locationId,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            consumption: parseFloat(literal.consumption),
            tip: parseFloat(literal.tip),
            deliveryFee: parseFloat(literal.deliveryFee),
            date: DateTime.parseIsoDate(literal.date),
            locationName: literal.locationName,
            logoPath: literal.logoPath == null ? undefined : literal.logoPath,
            tableName: literal.tableName,
            orderServiceId: literal.orderServiceId,
            state: parseInt(literal.state),
            preorder: literal.preorder,
            effectiveFulfillmentReadyDateTime:
                literal.effectiveFulfillmentReadyDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveFulfillmentReadyDateTime),
            payLater: literal.payLater,
            scheduledFulfillmentDateTime:
                literal.scheduledFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.scheduledFulfillmentDateTime),
            cancellationState: literal.cancellationState == null ? undefined : parseInt(literal.cancellationState),
            deliveryAddress: literal.deliveryAddress == null ? undefined : CustomerDao.parseDeliveryAddress(literal.deliveryAddress),
        };
    }

    private static parseFullCustomerOrder(literal: any): IFullCustomerOrder {
        return {
            ...CustomerDao.parseCustomerOrder(literal),
            items: parseCollection(literal.items, OrderDao.parseOrderItem),
            currentOrderDiscountAmount: parseFloat(literal.currentOrderDiscountAmount),
            isTipCancelled: literal.isTipCancelled,
            totalNetAmountFromPaymentsDrafts: parseFloat(literal.totalNetAmountFromPaymentsDrafts),
            totalPaymentAmountFromPaymentDrafts: parseFloat(literal.totalPaymentAmountFromPaymentDrafts),
            totalPaidAmount: parseFloat(literal.totalPaidAmount),
            totalCancelledAmount: parseFloat(literal.totalCancelledAmount),
        };
    }

    private static parseCustomerOrderFulfillment(literal: any): ICustomerFulfillment {
        return {
            id: literal.id,
            orderId: literal.orderId,
            orderDate: DateTime.parseIsoDate(literal.orderDate),
            orderNumber: literal.orderNumber,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            locationName: literal.locationName,
            fulfillmentGroupId: literal.fulfillmentGroupId == null ? undefined : literal.fulfillmentGroupId,
            articles: parseInt(literal.articles),
            orderMainArticleQty: parseInt(literal.orderMainArticleQty),
            name: literal.name == null ? undefined : parseMultiLanguageText(literal.name),
            effectiveReadyDateTime: DateTime.parseIsoDate(literal.effectiveReadyDateTime),
            handoutDate: literal.handoutDate == null ? undefined : DateTime.parseIsoDate(literal.handoutDate),
            isFulfilled: literal.isFulfilled,
            tableName: literal.tableName,
            orderServiceId: literal.orderServiceId,
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
            state: parseInt(literal.state),
            currentQueuePosition:
                literal.currentQueuePosition == null ? undefined : parseInt(literal.currentQueuePosition),
            fulfillmentGroupOrder:
                literal.fulfillmentGroupOrder == null ? undefined : parseInt(literal.fulfillmentGroupOrder),
            preorder: literal.preorder,
            hideCurrentQueueIndicator: literal.hideCurrentQueueIndicator,
            isLinked: literal.isLinked,
            orderScheduledFulfillmentDateTime:
                literal.orderScheduledFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.orderScheduledFulfillmentDateTime),
            deliveryAddress: literal.deliveryAddress == null ? undefined : CustomerDao.parseDeliveryAddress(literal.deliveryAddress),
        };
    }

    private static parseCustomerOptionGroup(literal: any): ICustomerOptionGroup {
        return {
            optionGroupId: literal.optionGroupId,
            displayName: literal.displayName,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            minOptions: literal.minOptions == null ? undefined : parseInt(literal.minOptions),
            maxOptions: literal.maxOptions == null ? undefined : parseInt(literal.maxOptions),
            maxPerOption: literal.maxPerOption == null ? undefined : parseInt(literal.maxPerOption),
            maxSum: literal.maxSum == null ? undefined : parseInt(literal.maxSum),
            options: parseCollection(literal.options, CustomerDao.parseCustomerOptionGroupOption),
        };
    }

    private static parseCustomerOptionGroupOption(literal: any): ICustomerOptionGroupOption {
        return {
            articleId: literal.articleId,
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            optionGroups: parseCollection(literal.optionGroups, CustomerDao.parseCustomerOptionGroup),
            price: literal.price == null ? undefined : parseFloat(literal.price),
            oohPrice: literal.oohPrice == null ? undefined : parseFloat(literal.oohPrice),
            imagePath: literal.imagePath == null ? undefined : literal.imagePath,
            kioskImagePath: literal.kioskImagePath == null ? undefined : literal.kioskImagePath,
            preselected: literal.preselected,
            minQuantity: literal.minQuantity == null ? undefined : parseInt(literal.minQuantity),
            maxQuantity: literal.maxQuantity == null ? undefined : parseInt(literal.maxQuantity),
        };
    }

    public static parseBill(literal: any): IBill {
        return {
            id: literal.id,
            tableId: literal.tableId,
            opened: DateTime.parseIsoDate(literal.opened),
            closed: literal.closed != null ? DateTime.parseIsoDate(literal.closed) : undefined,
            total: Misc.parseFloat(literal.total)!,
            totalPaid: Misc.parseFloat(literal.totalPaid)!,
            unpaidItems: parseCollection(literal.unpaidItems, CustomerDao.parseBillItem),
            paidItems: parseCollection(literal.paidItems, CustomerDao.parseBillItem),
            selections: parseCollection(literal.selections, CustomerDao.parseUserItemSelection),
            customName: literal.customName == null ? undefined : literal.customName,
            type: parseInt(literal.type),
            manualEndUsersCount:
                literal.manualEndUsersCount == null ? undefined : parseInt(literal.manualEndUsersCount),

            totalUnpaidConsumption: Misc.parseFloat(literal.totalUnpaidConsumption)!,
            totalUnpaidDiscountsAmount: Misc.parseFloat(literal.totalUnpaidDiscountsAmount)!,
            totalUnpaidAmountAfterDiscounts: Misc.parseFloat(literal.totalUnpaidAmountAfterDiscounts)!,
            selectionDiscountAmount: Misc.parseFloat(literal.selectionDiscountAmount)!,
            selectionTotalAfterItemDiscounts: Misc.parseFloat(literal.selectionTotalAfterItemDiscounts)!,
            selectionTotalAfterDiscount: Misc.parseFloat(literal.selectionTotalAfterDiscount)!,
            selectionTotalDiscountsAndVouchersAmount: Misc.parseFloat(
                literal.selectionTotalDiscountsAndVouchersAmount
            )!,
            hasDiscount: literal.hasDiscount,
            selectionHasVoucher: literal.selectionHasVoucher,
            selectedQuantityToAmountMap: CustomerDao.parseSelectedQuantityToAmountMap(
                literal.selectedQuantityToAmountMap
            ),
            appliedDiscounts: parseCollection(literal.appliedDiscounts, CustomerDao.parseBillDiscount),
            appliedItemDiscounts: parseCollection(literal.appliedItemDiscounts, CustomerDao.parseBillItemDiscount),
            appliedItemDiscountSummaries: parseCollection(
                literal.appliedItemDiscountSummaries,
                CustomerDao.parseBillItemDiscountSummary
            ),
            appliedBillDiscountSummaries: parseCollection(
                literal.appliedBillDiscountSummaries,
                CustomerDao.parseBillDiscountSummary
            ),
        };
    }

    public static parseNullableBill(literal: any): IBill | undefined {
        if (literal === undefined || literal == null) return undefined;

        return CustomerDao.parseBill(literal);
    }

    public static parseBillItemTimerDiscount(literal: any): IBillItemTimerDiscount {
        return {
            discountType: parseInt(literal.discountType),
            percentage: literal.percentage == null ? undefined : Misc.parseFloat(literal.percentage),
            absoluteAmount: literal.absoluteAmount == null ? undefined : Misc.parseFloat(literal.absoluteAmount),
        };
    }

    private static parseBillItem(literal: any): IBillItem {
        return {
            id: literal.id,
            orderItemId: literal.orderItemId,
            orderId: literal.orderId,
            articleId: literal.articleId == null ? undefined : literal.articleId,
            name: literal.name != null ? parseMultiLanguageText(literal.name) : undefined,
            internalName: literal.internalName != null ? literal.internalName : undefined,
            comment: literal.comment != null ? OrderDao.parseOrderItemComment(literal.comment) : undefined,
            isPricePerMinute: literal.isPricePerMinute === true,
            // UTC, parseIsoDate takes the missing Z of the server into account
            timerStartedDateTime:
                literal.timerStartedDateTime == null ? undefined : DateTime.parseIsoDate(literal.timerStartedDateTime),
            timerStoppedDateTime:
                literal.timerStoppedDateTime == null ? undefined : DateTime.parseIsoDate(literal.timerStoppedDateTime),
            timerElapsedSeconds: literal.timerElapsedSeconds ?? 0,
            timerElapsedReadDateTime: new Date(),
            timerMinuteRate: literal.timerMinuteRate == null ? undefined : Misc.parseFloat(literal.timerMinuteRate),
            timerDiscounts: parseCollection(literal.timerDiscounts, CustomerDao.parseBillItemTimerDiscount),
            quantity: parseInt(literal.quantity),
            paidQuantity: parseInt(literal.paidQuantity),
            unpaidQuantity: parseInt(literal.unpaidQuantity),
            paidAmount: Misc.parseFloat(literal.paidAmount)!,
            unitPrice: Misc.parseFloat(literal.unitPrice)!,
            unitPriceWithOptions: Misc.parseFloat(literal.unitPriceWithOptions)!,
            isZeroPriceItem: literal.isZeroPriceItem,
            sortOrder: parseInt(literal.sortOrder),
            options: parseCollection(literal.options, CustomerDao.parseBillItemOptionGroup),
            unitPriceAfterDiscount: Misc.parseFloat(literal.unitPriceAfterDiscount)!,
            unitPriceAfterDiscountWithOptions: Misc.parseFloat(literal.unitPriceAfterDiscountWithOptions)!,
            lastDiscountType: literal.lastDiscountType == null ? undefined : parseInt(literal.lastDiscountType),
            hasDiscount: literal.hasDiscount,
            hasProcessedItemLevelDiscount: literal.hasProcessedItemLevelDiscount,
        };
    }

    private static parseBillItemOptionGroup(literal: any): IBillItemOptionGroup {
        return {
            id: literal.id,
            displayName: literal.displayName,
            internalName: literal.internalName != null ? literal.internalName : undefined,
            name: literal.name != null ? parseMultiLanguageText(literal.name) : undefined,
            items: parseCollection(literal.items, CustomerDao.parseBillItemOption),
        };
    }

    private static parseBillItemOption(literal: any): IBillItemOption {
        return {
            id: literal.id,
            articleId: literal.articleId == null ? undefined : literal.articleId,
            quantity: parseFloat(literal.quantity),
            name: literal.name != null ? parseMultiLanguageText(literal.name) : undefined,
            internalName: literal.internalName != null ? literal.internalName : undefined,
            unitPrice: Misc.parseFloat(literal.unitPrice)!,
            options: parseCollection(literal.options, CustomerDao.parseBillItemOptionGroup),
            unitPriceAfterDiscount: Misc.parseFloat(literal.unitPriceAfterDiscount)!,
            lastDiscountType: literal.lastDiscountType == null ? undefined : parseInt(literal.lastDiscountType),
            hasDiscount: literal.hasDiscount,
            hasProcessedItemLevelDiscount: literal.hasProcessedItemLevelDiscount,
            appliedDiscountSplits:
                literal.appliedDiscountSplits != null
                    ? parseCollection(literal.appliedDiscountSplits, CustomerDao.parseBillDiscountItemSplit)
                    : [],
        };
    }

    private static parseUserItemSelection(literal: any): IUserItemSelection {
        return {
            billItemId: literal.billItemId,
            quantity: parseInt(literal.quantity),
            identityId: literal.identityId,
        };
    }

    private static parseCustomerPayment(literal: any): ICustomerPayment {
        return {
            ...OrderDao.parsePayment(literal),
            userId: literal.userId == null ? undefined : literal.userId,
            userName: literal.userName == null ? undefined : literal.userName,
            locationId: literal.locationId == null ? undefined : literal.locationId,
            locationName: literal.locationName == null ? undefined : literal.locationName,
            locationLogoPath: literal.locationLogoPath == null ? undefined : literal.locationLogoPath,
            items: parseCollection(literal.items, CustomerDao.parseCustomerPaymentItem),
            vatGroups: parseCollection(literal.vatGroups, OrderDao.parseVatGroupDto),
            consumption: parseFloat(literal.consumption),
            orderLevelDiscountAmount: parseFloat(literal.orderLevelDiscountAmount),
            totalPaid: parseFloat(literal.totalPaid),
            transactionOrders: parseCollection(literal.transactionOrders ?? [], (c) =>
                OrderDao.parseTransactionOrder(c)
            ),
        };
    }

    private static parseCustomerPaymentItem(literal: any): ICustomerPaymentItem {
        return {
            id: literal.id,
            amount: parseFloat(literal.amount),
            quantity: parseInt(literal.quantity),
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            hasDiscount: literal.hasDiscount,
            lastDiscountType: literal.lastDiscountType == null ? undefined : parseInt(literal.lastDiscountType),
            totalAmount: parseFloat(literal.totalAmount),
            totalAmountAfterDiscount: parseFloat(literal.totalAmountAfterDiscount),
            options: parseCollection(literal.options, OrderDao.parsePaymentItemOptionGroup),
        };
    }

    static parseFulfillmentDateSlot(literal: any): IFulfillmentDateSlotDto {
        return {
            locationId: literal.locationId,
            date: DateTime.parseIsoDate(literal.date),
            timeSlots: parseCollection(literal.timeSlots, CustomerDao.parseFulfillmentTimeSlot),
            isAvailable: literal.isAvailable,
        };
    }

    static parseFulfillmentTimeSlot(literal: any): IFulfillmentTimeSlotDto {
        return {
            startTime: literal.startTime,
            endTime: literal.endTime,
            offerId: literal.offerId,
            availableCapacity: literal.availableCapacity == null ? undefined : parseInt(literal.availableCapacity),
            isAvailable: literal.isAvailable,
        };
    }

    static parseServicePaymentMethods(literal: any): IServicePaymentMethods {
        return {
            serviceId: literal.serviceId,
            paymentMethods: parseCollection(literal.paymentMethods, PlanningDao.parsePaymentMethod),
            quickPaymentMethods: parseCollection(literal.quickPaymentMethods, PlanningDao.parsePaymentMethod),
        };
    }

    private static parseSelectedQuantityToAmountMap(literal: any): Map<string, IBillItemSelectionAmounts[]> {
        const map = new Map<string, IBillItemSelectionAmounts[]>();
        const obj = literal || {};
        for (const key in obj) {
            map.set(key, parseCollection(obj[key], CustomerDao.parseBillItemSelectionAmounts));
        }
        return map;
    }

    private static parseBillItemSelectionAmounts(literal: any): IBillItemSelectionAmounts {
        return {
            selectedQuantity: parseInt(literal.selectedQuantity),
            totalAfterItemDiscounts: parseFloat(literal.totalAfterItemDiscounts),
            billDiscountsAndVouchersAmount: parseFloat(literal.billDiscountsAndVouchersAmount),
            totalDiscountsAndVouchersAmount: parseFloat(literal.totalDiscountsAndVouchersAmount),
            totalAfterBillDiscountsAndVouchers: parseFloat(literal.totalAfterBillDiscountsAndVouchers),
            totalAfterBillDiscounts: parseFloat(literal.totalAfterBillDiscounts),
        };
    }

    private static parseBillDiscount(literal: any): IBillDiscount {
        return {
            id: literal.id,
            discountId: literal.discountId,
            voucherId: literal.voucherId,
            identityId: literal.identityId,
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
            amount: Misc.parseFloat(literal.amount)!,
            isProcessed: literal.isProcessed,
            discountName: literal.discountName != null ? parseMultiLanguageText(literal.discountName) : undefined,
            voucherCode: literal.voucherCode != null ? literal.voucherCode : undefined,
        };
    }

    private static parseBillItemDiscount(literal: any): IBillItemDiscount {
        return {
            id: literal.id,
            billItemId: literal.billItemId,
            discountId: literal.discountId,
            discountType: parseInt(literal.discountType),
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
            unitDiscountAmount: Misc.parseFloat(literal.unitDiscountAmount)!,
            isProcessed: literal.isProcessed,
        };
    }

    private static parseBillItemDiscountSummary(literal: any): IBillItemDiscountSummary {
        return {
            discountId: literal.discountId,
            discountType: parseInt(literal.discountType),
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
            isProcessed: literal.isProcessed,
            discountName: parseMultiLanguageText(literal.discountName),
            appliedAmount: parseFloat(literal.appliedAmount),
        };
    }

    private static parseBillDiscountSummary(literal: any): IBillDiscountSummary {
        return {
            discountId: literal.discountId,
            appliedAmount: parseFloat(literal.appliedAmount),
        };
    }

    private static parseCartDiscountsSummary(literal: any): ICartDiscountsSummary {
        return {
            appliedItemDiscountSummaries: parseCollection(
                literal.appliedItemDiscountSummaries,
                CustomerDao.parseCartItemDiscountSummary
            ),
            appliedDiscounts: parseCollection(literal.appliedDiscounts, CustomerDao.parseCartDiscount),
            appliedVouchers: parseCollection(literal.appliedVouchers, CustomerDao.parseCartDiscount),
        };
    }

    private static parseCartItemDiscountSummary(literal: any): ICartItemDiscountSummary {
        return {
            discountId: literal.discountId,
            discountType: parseInt(literal.discountType),
            discountName: parseMultiLanguageText(literal.discountName),
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
            appliedAmount: parseFloat(literal.appliedAmount),
        };
    }

    private static parseCartDiscount(literal: any): ICartDiscount {
        return {
            id: literal.id,
            voucherId: literal.voucherId == null ? undefined : literal.voucherId,
            discountId: literal.discountId == null ? undefined : literal.discountId,
            amount: parseFloat(literal.amount),
            discountName: literal.discountName != null ? parseMultiLanguageText(literal.discountName) : undefined,
            voucherCode: literal.voucherCode != null ? literal.voucherCode : undefined,
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
        };
    }

    private static parseBillDiscountItemSplit(literal: any): IBillDiscountItemSplit {
        return {
            id: literal.id,
            billDiscountId: literal.billDiscountId,
            billItemId: literal.billItemId,
            discountedQuantity: parseInt(literal.discountedQuantity),
            totalDiscountAmount: parseFloat(literal.totalDiscountAmount),
            isVoucherSplit: literal.isVoucherSplit,
            isProcessed: literal.isProcessed,
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
        };
    }

    public static parseCustomerArticleResource(literal: any): ICustomerArticleResource {
        return {
            id: literal.id,
            resourceId: literal.resourceId,
            predefinedResourceCode:
                literal.predefinedResourceCode == null ? undefined : parseInt(literal.predefinedResourceCode),
            numberOfUnits: parseFloat(literal.numberOfUnits),
        };
    }

    public static parseCustomerArticleAttribute(literal: any): ICustomerArticleAttribute {
        return {
            id: literal.id,
            name: parseMultiLanguageText(literal.name),
            effect: literal.effect == null ? undefined : parseInt(literal.effect),
            sortOrder: parseInt(literal.sortOrder),
            displayType: literal.displayType == null ? AttributeDisplayType.None : parseInt(literal.displayType),
            iconClass: literal.iconClass == null ? undefined : literal.iconClass,
            flagDisplayTo: literal.flagDisplayTo == null ? undefined : parseInt(literal.flagDisplayTo),
        };
    }
}
